import React from 'react';
import '../styles/policy.css';

const PrivacyPolicy = ({onClose}) => {
    const currentYear = new Date().getFullYear();
    return (
        <div className="modal">
            <div className="modal-content policy-section">
                <span className="close" onClick={onClose}>&times;</span>
                <h1>Privacy Policy</h1>
                <p><strong>Effective Date:</strong> Tue Aug 19 2025</p>
                <p><strong>Last Updated:</strong> Fri Aug 22 2025</p>

                <p>Intellommerce.ai. ("Intellommerce", "we", "us", or "our") is committed to protecting your privacy and
                    ensuring the security of your personal information. This comprehensive Privacy Policy explains in
                    detail how we collect, use, disclose, store, and safeguard your information when you visit our
                    website, use our automated digital transformation services, or interact with us in any capacity.
                    This policy is designed to comply with applicable data protection laws including the General Data
                    Protection Regulation (GDPR), California Consumer Privacy Act (CCPA) as amended by the California
                    Privacy Rights Act (CPRA), and other relevant privacy regulations worldwide.</p>

                <h2>1. Information We Collect</h2>

                <h3>1.1 Personal Information We Collect Directly From You</h3>
                <ul>
                    <li><strong>Contact Information:</strong> Full name, email address, phone number, mailing address,
                        company name, job title, department
                    </li>
                    <li><strong>Account Information:</strong> Username, password, account preferences, profile
                        information
                    </li>
                    <li><strong>Business Information:</strong> Company size, industry sector, business needs, project
                        requirements
                    </li>
                    <li><strong>Communication Data:</strong> Records of your communications with us including emails,
                        support tickets, feedback, survey responses
                    </li>
                    <li><strong>Payment Information:</strong> Billing address, payment method details (processed
                        securely through third-party payment processors)
                    </li>
                    <li><strong>Professional Information:</strong> Resume/CV data, professional qualifications, LinkedIn
                        profile information
                    </li>
                </ul>

                <h3>1.2 Information We Collect Automatically</h3>
                <ul>
                    <li><strong>Device Information:</strong> IP address, device type, operating system, browser type and
                        version, device identifiers, mobile network information
                    </li>
                    <li><strong>Usage Data:</strong> Pages visited, links clicked, features used, time spent on pages,
                        access dates and times, referring website addresses
                    </li>
                    <li><strong>Location Data:</strong> Approximate geographic location based on IP address, time zone
                    </li>
                    <li><strong>Log Data:</strong> Server logs, error reports, performance data, system diagnostics</li>
                    <li><strong>Analytics Data:</strong> Aggregated usage patterns, feature adoption metrics, user
                        journey data
                    </li>
                </ul>

                <h3>1.3 Information From Third-Party Sources</h3>
                <ul>
                    <li><strong>Business Intelligence Providers:</strong> Company information, industry data, financial
                        information
                    </li>
                    <li><strong>Marketing Partners:</strong> Lead generation data, marketing qualified leads, campaign
                        performance data
                    </li>
                    <li><strong>Social Media Platforms:</strong> Public profile information when you interact with our
                        social media pages
                    </li>
                    <li><strong>Integration Partners:</strong> Data from connected third-party applications and services
                        you authorize
                    </li>
                </ul>

                <h3>1.4 Cookies and Similar Technologies</h3>
                <ul>
                    <li><strong>Essential Cookies:</strong> Required for website functionality, security, and
                        performance
                    </li>
                    <li><strong>Analytics Cookies:</strong> Google Analytics, Adobe Analytics for usage analysis and
                        improvement
                    </li>
                    <li><strong>Marketing Cookies:</strong> Advertising effectiveness, retargeting, conversion tracking
                    </li>
                    <li><strong>Preference Cookies:</strong> Language preferences, display settings, personalization
                    </li>
                    <li><strong>Session Cookies:</strong> Temporary cookies deleted when you close your browser</li>
                    <li><strong>Persistent Cookies:</strong> Remain on your device for a set period</li>
                    <li><strong>Pixel Tags/Web Beacons:</strong> Small graphics for tracking email opens and website
                        visits
                    </li>
                </ul>

                <h2>2. Legal Basis for Processing (GDPR)</h2>
                <p>We process your personal data based on the following legal grounds:</p>
                <ul>
                    <li><strong>Contract Performance:</strong> Processing necessary to provide our services and fulfill
                        our contractual obligations
                    </li>
                    <li><strong>Legitimate Interests:</strong> Processing for business operations, service improvement,
                        fraud prevention, and network security
                    </li>
                    <li><strong>Legal Compliance:</strong> Processing required by law, regulation, or legal proceedings
                    </li>
                    <li><strong>Consent:</strong> Processing based on your explicit consent for marketing communications
                        and optional features
                    </li>
                    <li><strong>Vital Interests:</strong> Processing necessary to protect vital interests in emergency
                        situations
                    </li>
                </ul>

                <h2>3. How We Use Your Information</h2>

                <h3>3.1 Service Delivery and Operations</h3>
                <ul>
                    <li>Providing automated digital transformation services and solutions</li>
                    <li>Managing user accounts and authentication</li>
                    <li>Processing transactions and billing</li>
                    <li>Delivering customer support and technical assistance</li>
                    <li>Customizing and personalizing your experience</li>
                    <li>Operating and maintaining our infrastructure</li>
                </ul>

                <h3>3.2 Communication and Marketing</h3>
                <ul>
                    <li>Sending service-related notifications and updates</li>
                    <li>Providing newsletters, promotional materials, and offers (with consent)</li>
                    <li>Conducting surveys and gathering feedback</li>
                    <li>Responding to inquiries and requests</li>
                    <li>Sending transactional emails and confirmations</li>
                </ul>

                <h3>3.3 Analytics and Improvement</h3>
                <ul>
                    <li>Analyzing usage patterns and trends</li>
                    <li>Improving our services and developing new features</li>
                    <li>Conducting research and development</li>
                    <li>Testing and quality assurance</li>
                    <li>Measuring marketing effectiveness</li>
                </ul>

                <h3>3.4 Security and Compliance</h3>
                <ul>
                    <li>Detecting, preventing, and addressing fraud and security issues</li>
                    <li>Complying with legal obligations and regulations</li>
                    <li>Enforcing our terms of service and policies</li>
                    <li>Protecting rights, property, and safety</li>
                    <li>Maintaining audit trails and compliance records</li>
                </ul>

                <h3>3.5 Automated Decision-Making and AI Processing</h3>
                <ul>
                    <li>Providing AI-powered recommendations and insights</li>
                    <li>Automating business processes and workflows</li>
                    <li>Predictive analytics and forecasting</li>
                    <li>Pattern recognition and anomaly detection</li>
                    <li>Machine learning model training (using aggregated/anonymized data)</li>
                </ul>

                <h2>4. Sharing and Disclosure of Information</h2>

                <h3>4.1 Service Providers and Business Partners</h3>
                <ul>
                    <li><strong>Cloud Infrastructure Providers:</strong> AWS, Microsoft Azure, Google Cloud for hosting
                        and data storage
                    </li>
                    <li><strong>Payment Processors:</strong> Stripe, PayPal, or similar services for secure payment
                        processing
                    </li>
                    <li><strong>Analytics Providers:</strong> Google Analytics, Mixpanel, Segment for usage analytics
                    </li>
                    <li><strong>Customer Support Tools:</strong> Zendesk, Intercom for customer service management</li>
                    <li><strong>Marketing Platforms:</strong> HubSpot, Salesforce, Mailchimp for marketing automation
                    </li>
                    <li><strong>Security Services:</strong> Cloudflare, security monitoring, and threat detection
                        services
                    </li>
                    <li><strong>Professional Advisors:</strong> Lawyers, accountants, auditors bound by confidentiality
                    </li>
                </ul>

                <h3>4.2 Business Transfers</h3>
                <p>In the event of a merger, acquisition, reorganization, bankruptcy, or sale of assets, your
                    information may be transferred as part of that transaction. We will notify you via email and/or
                    prominent notice on our website of any change in ownership or uses of your personal information.</p>

                <h3>4.3 Legal Requirements and Protection</h3>
                <p>We may disclose your information when required by law or when we believe in good faith that
                    disclosure is necessary to:</p>
                <ul>
                    <li>Comply with legal obligations, subpoenas, or court orders</li>
                    <li>Respond to lawful requests from public authorities</li>
                    <li>Protect and defend our rights, property, or safety</li>
                    <li>Prevent or investigate possible wrongdoing</li>
                    <li>Protect the personal safety of users or the public</li>
                    <li>Protect against legal liability</li>
                </ul>

                <h3>4.4 Consent-Based Sharing</h3>
                <ul>
                    <li>With third parties you explicitly authorize through integrations</li>
                    <li>When you participate in public forums or communities</li>
                    <li>For testimonials or case studies (with your explicit consent)</li>
                    <li>When you direct us to share information with specific parties</li>
                </ul>

                <h3>4.5 Aggregated and Anonymized Data</h3>
                <p>We may share aggregated, anonymized, or de-identified information that cannot reasonably be used to
                    identify you for research, marketing, or other business purposes.</p>

                <h2>5. Data Protection and Security</h2>

                <h3>5.1 Technical and Organizational Measures</h3>
                <ul>
                    <li><strong>Encryption:</strong> AES-256 encryption for data at rest and TLS 1.3 for data in transit
                    </li>
                    <li><strong>Access Controls:</strong> Role-based access control, multi-factor authentication,
                        principle of least privilege
                    </li>
                    <li><strong>Network Security:</strong> Firewalls, intrusion detection systems, DDoS protection</li>
                    <li><strong>Physical Security:</strong> Secure data centers with 24/7 monitoring and access controls
                    </li>
                    <li><strong>Regular Audits:</strong> Security assessments, penetration testing, vulnerability
                        scanning
                    </li>
                    <li><strong>Incident Response:</strong> Comprehensive incident response plan and breach notification
                        procedures
                    </li>
                    <li><strong>Employee Training:</strong> Regular privacy and security awareness training for all
                        staff
                    </li>
                    <li><strong>Data Minimization:</strong> Collecting only necessary data and implementing retention
                        policies
                    </li>
                </ul>

                <h3>5.2 Compliance Certifications</h3>
                <ul>
                    <li>SOC 2 Type II certification</li>
                    <li>ISO 27001 information security management</li>
                    <li>Privacy Shield frameworks (where applicable)</li>
                    <li>Industry-specific compliance standards</li>
                </ul>

                <h3>5.3 Data Breach Procedures</h3>
                <p>In the event of a data breach that poses a risk to your rights and freedoms, we will:</p>
                <ul>
                    <li>Notify affected individuals within 72 hours (as required by GDPR)</li>
                    <li>Notify relevant supervisory authorities</li>
                    <li>Document the breach and our response</li>
                    <li>Take immediate steps to mitigate harm</li>
                    <li>Conduct a thorough investigation</li>
                    <li>Implement measures to prevent future breaches</li>
                </ul>

                <h2>6. Your Privacy Rights</h2>

                <h3>6.1 Rights Under GDPR (European Economic Area, UK, Switzerland)</h3>
                <ul>
                    <li><strong>Right to Access:</strong> Request a copy of your personal data we hold</li>
                    <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data
                    </li>
                    <li><strong>Right to Erasure ("Right to be Forgotten"):</strong> Request deletion of your personal
                        data
                    </li>
                    <li><strong>Right to Restrict Processing:</strong> Request limitation of processing in certain
                        circumstances
                    </li>
                    <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable
                        format
                    </li>
                    <li><strong>Right to Object:</strong> Object to processing based on legitimate interests or direct
                        marketing
                    </li>
                    <li><strong>Rights Related to Automated Decision-Making:</strong> Not be subject to solely automated
                        decisions
                    </li>
                    <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is
                        based on consent
                    </li>
                    <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local supervisory
                        authority
                    </li>
                </ul>

                <h3>6.2 Rights Under CCPA/CPRA (California Residents)</h3>
                <ul>
                    <li><strong>Right to Know:</strong> Request information about personal information collected, used,
                        disclosed, or sold
                    </li>
                    <li><strong>Right to Delete:</strong> Request deletion of personal information we hold about you
                    </li>
                    <li><strong>Right to Opt-Out:</strong> Opt-out of the sale or sharing of personal information</li>
                    <li><strong>Right to Non-Discrimination:</strong> Not be discriminated against for exercising
                        privacy rights
                    </li>
                    <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
                    <li><strong>Right to Limit Use:</strong> Limit use and disclosure of sensitive personal information
                    </li>
                    <li><strong>Right to Data Portability:</strong> Request your data in a portable format</li>
                </ul>

                <h3>6.3 Rights Under Other Privacy Laws</h3>
                <ul>
                    <li><strong>Brazilian Residents (LGPD):</strong> Similar rights to GDPR including access,
                        correction, deletion, and portability
                    </li>
                    <li><strong>Canadian Residents (PIPEDA):</strong> Rights to access, correct, and withdraw consent
                    </li>
                    <li><strong>Australian Residents (Privacy Act):</strong> Rights to access, correct, and complain
                        about privacy breaches
                    </li>
                    <li><strong>Other U.S. States:</strong> Rights under Virginia (VCDPA), Colorado (CPA), Utah (UCPA),
                        and Connecticut (CTDPA) privacy laws
                    </li>
                </ul>

                <h3>6.4 How to Exercise Your Rights</h3>
                <p>To exercise any of your privacy rights, please contact us using the following methods:</p>
                <ul>
                    <li><strong>Email:</strong> privacy@intellommerce.ai</li>
                </ul>

                <p>We will respond to your request within the timeframes required by applicable law (typically within 30
                    days). We may need to verify your identity before processing your request.</p>

                <h2>7. International Data Transfers</h2>

                <h3>7.1 Transfer Mechanisms</h3>
                <p>Your information may be transferred to and processed in countries outside your jurisdiction. We
                    ensure appropriate safeguards through:</p>
                <ul>
                    <li><strong>Standard Contractual Clauses (SCCs):</strong> EU-approved contractual terms for data
                        transfers
                    </li>
                    <li><strong>Adequacy Decisions:</strong> Transfers to countries deemed adequate by the European
                        Commission
                    </li>
                    <li><strong>Binding Corporate Rules:</strong> For intra-group transfers where applicable</li>
                    <li><strong>Your Consent:</strong> Where other safeguards are not available</li>
                </ul>

                <h3>7.2 Data Localization</h3>
                <p>Where required by local law, we maintain data within specific geographic regions and comply with data
                    localization requirements.</p>

                <h2>8. Data Retention</h2>

                <h3>8.1 Retention Periods</h3>
                <ul>
                    <li><strong>Account Information:</strong> Duration of account plus 7 years for legal/tax purposes
                    </li>
                    <li><strong>Transaction Records:</strong> 7 years for financial and tax compliance</li>
                    <li><strong>Marketing Data:</strong> 3 years from last interaction or until consent withdrawn</li>
                    <li><strong>Usage Analytics:</strong> 2 years in identifiable form, then aggregated/anonymized</li>
                    <li><strong>Security Logs:</strong> 1 year for security and compliance purposes</li>
                    <li><strong>Cookie Data:</strong> As specified in our Cookie Policy (typically 13 months maximum)
                    </li>
                </ul>

                <h3>8.2 Retention Criteria</h3>
                <p>We determine retention periods based on:</p>
                <ul>
                    <li>Legal and regulatory requirements</li>
                    <li>Contractual obligations</li>
                    <li>Business needs and legitimate interests</li>
                    <li>Statute of limitations for legal claims</li>
                    <li>Your consent and preferences</li>
                </ul>

                <h2>9. Children's Privacy</h2>

                <p>Our services are not directed to individuals under the age of 16 (or applicable age of digital
                    consent in your jurisdiction). We do not knowingly collect personal information from children. If we
                    become aware that we have collected personal information from a child without appropriate consent,
                    we will take steps to delete that information as quickly as possible. If you believe we have
                    collected information from a child, please contact us immediately at privacy@intellommerce.com.</p>

                <h2>10. Third-Party Links and Services</h2>

                <h3>10.1 External Websites</h3>
                <p>Our website may contain links to third-party websites, services, or applications that are not
                    operated by us. We are not responsible for the privacy practices of these third parties. We
                    encourage you to review their privacy policies before providing any personal information.</p>

                <h3>10.2 Third-Party Integrations</h3>
                <p>When you connect third-party services to our platform:</p>
                <ul>
                    <li>We only access information necessary for the integration</li>
                    <li>We do not control the third party's data practices</li>
                    <li>You should review the third party's privacy policy</li>
                    <li>You can disconnect integrations at any time through your account settings</li>
                </ul>

                <h3>10.3 Social Media Features</h3>
                <p>Our services may include social media features (e.g., share buttons, widgets). These features may
                    collect information about your interactions and may set cookies. These features are governed by the
                    privacy policies of the respective social media platforms.</p>

                <h2>11. Marketing and Communications</h2>

                <h3>11.1 Marketing Preferences</h3>
                <p>You can manage your marketing preferences through:</p>
                <ul>
                    <li><strong>Email Preferences Center:</strong> Customize which types of emails you receive</li>
                    <li><strong>Unsubscribe Links:</strong> One-click unsubscribe in all marketing emails</li>
                    <li><strong>Account Settings:</strong> Manage all communication preferences</li>
                    <li><strong>Browser Settings:</strong> Control cookie-based advertising</li>
                </ul>

                <h3>11.2 Do Not Track Signals</h3>
                <p>Some browsers offer a "Do Not Track" (DNT) signal. Currently, there is no industry standard for
                    responding to DNT signals. We do not currently respond to DNT signals, but we provide other methods
                    to control data collection and use as described in this policy.</p>

                <h3>11.3 Targeted Advertising</h3>
                <p>We may participate in targeted advertising. You can opt-out through:</p>
                <ul>
                    <li>Network Advertising Initiative (NAI): optout.networkadvertising.org</li>
                    <li>Digital Advertising Alliance (DAA): optout.aboutads.info</li>
                    <li>European Digital Advertising Alliance: youronlinechoices.eu</li>
                    <li>Google Ads Settings: google.com/settings/ads</li>
                </ul>

                <h2>12. Automated Decision-Making and Profiling</h2>

                <h3>12.1 How We Use Automated Processing</h3>
                <p>As an automated digital transformation company, we use AI and machine learning for:</p>
                <ul>
                    <li>Service recommendations and personalization</li>
                    <li>Risk assessment and fraud detection</li>
                    <li>Performance optimization</li>
                    <li>Predictive analytics and insights</li>
                    <li>Automated workflow management</li>
                </ul>

                <h3>12.2 Your Rights Regarding Automated Processing</h3>
                <p>Under GDPR and similar laws, you have the right to:</p>
                <ul>
                    <li>Be informed about automated decision-making</li>
                    <li>Request human intervention in automated decisions</li>
                    <li>Express your point of view and contest decisions</li>
                    <li>Opt-out of certain types of automated processing</li>
                </ul>

                <h3>12.3 Safeguards and Transparency</h3>
                <ul>
                    <li>We implement measures to ensure fairness and accuracy</li>
                    <li>Regular audits of our algorithms for bias and errors</li>
                    <li>Clear explanations of how automated decisions are made</li>
                    <li>Human oversight for significant decisions</li>
                </ul>

                <h2>13. Cookie Policy</h2>

                <h3>13.1 Types of Cookies We Use</h3>
                <table border="1" style={{width: '100%', borderCollapse: 'collapse', margin: '10px 0'}}>
                    <tr>
                        <th style={{padding: '10px'}}>Cookie Type</th>
                        <th style={{padding: '10px'}}>Purpose</th>
                        <th style={{padding: '10px'}}>Duration</th>
                    </tr>
                    <tr>
                        <td style={{padding: '10px'}}>Essential</td>
                        <td style={{padding: '10px'}}>Website functionality, security, load balancing</td>
                        <td style={{padding: '10px'}}>Session or up to 1 year</td>
                    </tr>
                    <tr>
                        <td style={{padding: '10px'}}>Performance</td>
                        <td style={{padding: '10px'}}>Analytics, error tracking, performance monitoring</td>
                        <td style={{padding: '10px'}}>Up to 2 years</td>
                    </tr>
                    <tr>
                        <td style={{padding: '10px'}}>Functionality</td>
                        <td style={{padding: '10px'}}>User preferences, language settings, personalization</td>
                        <td style={{padding: '10px'}}>Up to 1 year</td>
                    </tr>
                    <tr>
                        <td style={{padding: '10px'}}>Marketing</td>
                        <td style={{padding: '10px'}}>Ad targeting, campaign measurement, remarketing</td>
                        <td style={{padding: '10px'}}>Up to 13 months</td>
                    </tr>
                </table>

                <h3>13.2 Managing Cookies</h3>
                <p>You can control cookies through:</p>
                <ul>
                    <li><strong>Cookie Banner:</strong> Manage preferences when you first visit our site</li>
                    <li><strong>Browser Settings:</strong> Block or delete cookies through your browser</li>
                    <li><strong>Cookie Settings:</strong> Access our cookie preference center anytime</li>
                    <li><strong>Mobile Settings:</strong> Manage app permissions on your device</li>
                </ul>

                <h3>13.3 Consequences of Disabling Cookies</h3>
                <p>Disabling certain cookies may impact your experience:</p>
                <ul>
                    <li>Essential cookies: Website may not function properly</li>
                    <li>Performance cookies: We cannot improve our services based on usage</li>
                    <li>Functionality cookies: Loss of personalization and saved preferences</li>
                    <li>Marketing cookies: You may still see ads, but they won't be personalized</li>
                </ul>

                <h2>14. California Privacy Rights (CCPA/CPRA)</h2>

                <h3>14.1 Notice at Collection</h3>
                <p>California residents have the right to know what personal information we collect. We collect the
                    following categories:</p>
                <ul>
                    <li><strong>Identifiers:</strong> Name, email, phone, IP address, device IDs</li>
                    <li><strong>Commercial Information:</strong> Purchase history, service usage, preferences</li>
                    <li><strong>Internet Activity:</strong> Browsing history, search history, interaction with our
                        services
                    </li>
                    <li><strong>Geolocation Data:</strong> Approximate location based on IP address</li>
                    <li><strong>Professional Information:</strong> Employment details, company information</li>
                    <li><strong>Inferences:</strong> Profiles based on your preferences and behavior</li>
                </ul>

                <h3>14.2 Sale and Sharing of Personal Information</h3>
                <p>We do not sell personal information as defined by CCPA. We may share information for business
                    purposes with:</p>
                <ul>
                    <li>Service providers under written contracts</li>
                    <li>Analytics and advertising partners (you can opt-out)</li>
                    <li>Business partners for joint offerings (with your consent)</li>
                </ul>

                <h3>14.3 Shine the Light Law</h3>
                <p>California residents may request information about personal information shared with third parties for
                    direct marketing purposes. Contact us at privacy@intellommerce.com to make such a request.</p>

                <h2>15. Special Categories of Data</h2>

                <h3>15.1 Sensitive Personal Information</h3>
                <p>We generally do not collect sensitive personal information (health data, biometric data,
                    racial/ethnic origin, political opinions, religious beliefs, etc.) unless:</p>
                <ul>
                    <li>You explicitly provide it with clear consent</li>
                    <li>It's necessary for legal compliance</li>
                    <li>It's needed for specific services you request</li>
                </ul>

                <h3>15.2 Financial Information</h3>
                <p>Payment information is processed by PCI-compliant third-party processors. We do not store complete
                    credit card numbers or banking details on our servers.</p>

                <h2>16. Privacy by Design</h2>
                <p>We incorporate privacy considerations into our services from the ground up:</p>
                <ul>
                    <li><strong>Data Minimization:</strong> Collecting only what's necessary</li>
                    <li><strong>Purpose Limitation:</strong> Using data only for stated purposes</li>
                    <li><strong>Privacy Impact Assessments:</strong> Evaluating privacy risks for new features</li>
                    <li><strong>Security by Default:</strong> Implementing strong security measures from the start</li>
                    <li><strong>Transparency:</strong> Clear communication about data practices</li>
                    <li><strong>User Control:</strong> Providing tools to manage your data</li>
                </ul>

                <h2>17. Accessibility</h2>
                <p>We are committed to making our privacy practices accessible to all users. If you need this privacy
                    policy in an alternative format or have accessibility-related questions, please contact us at
                    privacy@intellommerce.com.</p>

                <h2>18. Changes to This Privacy Policy</h2>
                <p>We may update this Privacy Policy periodically to reflect changes in our practices, technologies,
                    legal requirements, or for other operational reasons. When we make changes:</p>
                <ul>
                    <li>We will update the "Last Updated" date at the top</li>
                    <li>For material changes, we will provide notice through email or prominent website notice</li>
                    <li>We will obtain consent where required by applicable law</li>
                    <li>The new policy will apply to data collected after the effective date</li>
                </ul>

                <p>We encourage you to review this Privacy Policy periodically to stay informed about our data
                    practices.</p>

                <h2>19. Contact Information</h2>

                <h3>19.1 Contact for Privacy Inquiries</h3>
                <p>While a formal Data Protection Officer is not required
                for our operations, we take your privacy seriously. For any questions or concerns regarding this privacy
                policy or your data, please contact our designated team lead for privacy matters:</p>
                <p>
                <strong>Email:</strong> privacy@intellommerce.ai<br/>
                </p>

                <h3>19.2 Supervisory Authorities</h3>
                <p>You have the right to lodge a complaint with your local data protection authority:</p>
                <ul>
                    <li><strong>EU:</strong> Find your local authority at edpb.europa.eu</li>
                    <li><strong>UK:</strong> Information Commissioner's Office (ico.org.uk)</li>
                    <li><strong>California:</strong> California Privacy Protection Agency (cppa.ca.gov)</li>
                    <li><strong>Other Jurisdictions:</strong> Contact us for information about your local authority</li>
                </ul>

                <h2>20. Industry-Specific Provisions</h2>

                <h3>20.1 Enterprise Customers</h3>
                <p>For enterprise customers using our automated digital transformation services:</p>
                <ul>
                    <li>We act as a data processor for customer data processed through our platform</li>
                    <li>Data Processing Agreements (DPAs) are available upon request</li>
                    <li>We support customer compliance with their privacy obligations</li>
                    <li>Sub-processor lists are available in our Trust Center</li>
                    <li>We provide tools for data export and deletion</li>
                </ul>

                <h3>20.2 API and Developer Services</h3>
                <p>If you use our APIs or developer tools:</p>
                <ul>
                    <li>Additional terms apply as specified in our Developer Agreement</li>
                    <li>You must implement appropriate security measures</li>
                    <li>API usage is monitored for security and compliance</li>
                    <li>Rate limiting and access controls are enforced</li>
                </ul>

                <h3>20.3 AI and Machine Learning Services</h3>
                <p>For services involving AI/ML processing:</p>
                <ul>
                    <li>Model training uses aggregated and anonymized data where possible</li>
                    <li>Customer data is not used to improve models for other customers without permission</li>
                    <li>Explainability features are available for AI decisions</li>
                    <li>Bias monitoring and mitigation measures are implemented</li>
                </ul>

                <h2>21. Regional Privacy Notices</h2>

                <h3>21.1 European Economic Area, UK, and Switzerland</h3>
                <p>Additional provisions for EEA, UK, and Swiss residents:</p>
                <ul>
                    <li><strong>Legal Basis:</strong> We process data based on consent, contract, legal obligation,
                        vital interests, public task, or legitimate interests
                    </li>
                    <li><strong>Data Protection Officer:</strong> Contact our DPO at dpo@intellommerce.com</li>
                    <li><strong>International Transfers:</strong> We use Standard Contractual Clauses and other approved
                        mechanisms
                    </li>
                    <li><strong>Retention:</strong> We retain data no longer than necessary for the purposes collected
                    </li>
                </ul>

                <h3>21.2 Brazil (LGPD)</h3>
                <p>For Brazilian residents:</p>
                <ul>
                    <li>You have rights similar to GDPR including access, correction, deletion, and portability</li>
                    <li>We process data based on the legal bases in LGPD Article 7</li>
                    <li>Contact our Brazil privacy team at brazil-privacy@intellommerce.com</li>
                </ul>

                <h3>21.3 Asia-Pacific Region</h3>
                <p>Additional provisions for Asia-Pacific residents:</p>
                <ul>
                    <li><strong>Australia:</strong> We comply with the Australian Privacy Principles (APPs)</li>
                    <li><strong>Japan:</strong> We comply with the Act on Protection of Personal Information (APPI)</li>
                    <li><strong>Singapore:</strong> We comply with the Personal Data Protection Act (PDPA)</li>
                    <li><strong>South Korea:</strong> We comply with the Personal Information Protection Act (PIPA)</li>
                </ul>

                <h3>21.4 Other U.S. States</h3>
                <p>Privacy rights for residents of:</p>
                <ul>
                    <li><strong>Virginia (VCDPA):</strong> Rights to access, correct, delete, obtain copy, and opt-out
                    </li>
                    <li><strong>Colorado (CPA):</strong> Similar rights plus right to correct inaccuracies</li>
                    <li><strong>Connecticut (CTDPA):</strong> Comprehensive privacy rights effective July 2023</li>
                    <li><strong>Utah (UCPA):</strong> Basic privacy rights including access and deletion</li>
                    <li><strong>Nevada:</strong> Right to opt-out of sale of personal information</li>
                </ul>

                <h2>22. Data Processing Agreements</h2>
                <p>For business customers where we act as a data processor:</p>
                <ul>
                    <li>Standard Data Processing Agreements are available</li>
                    <li>EU Standard Contractual Clauses are incorporated</li>
                    <li>Custom DPAs available for enterprise customers</li>
                    <li>Sub-processor lists maintained and updated regularly</li>
                    <li>Security and compliance certifications provided</li>
                </ul>

                <h2>23. Privacy Shield (Historical Reference)</h2>
                <p>Although the EU-U.S. Privacy Shield was invalidated, we maintain our commitments to Privacy Shield
                    principles for personal data previously received in reliance on the Privacy Shield. We now rely on
                    Standard Contractual Clauses and other valid transfer mechanisms.</p>

                <h2>24. Additional Security Information</h2>

                <h3>24.1 Security Measures</h3>
                <p>We implement comprehensive security measures including:</p>
                <ul>
                    <li><strong>Encryption:</strong> TLS 1.3 for data in transit, AES-256 for data at rest</li>
                    <li><strong>Access Management:</strong> SSO, MFA, RBAC, privileged access management</li>
                    <li><strong>Monitoring:</strong> 24/7 security operations center, SIEM, threat intelligence</li>
                    <li><strong>Vulnerability Management:</strong> Regular scanning, penetration testing, bug bounty
                        program
                    </li>
                    <li><strong>Business Continuity:</strong> Disaster recovery, backup procedures, incident response
                    </li>
                </ul>

                <h3>24.2 Shared Responsibility Model</h3>
                <p>For platform services, security is a shared responsibility:</p>
                <ul>
                    <li><strong>Our Responsibility:</strong> Infrastructure, platform security, and compliance</li>
                    <li><strong>Your Responsibility:</strong> Access management, data classification, usage compliance
                    </li>
                </ul>

                <h2>25. Supplemental Terms</h2>

                <h3>25.1 Beta Services</h3>
                <p>For beta or preview services:</p>
                <ul>
                    <li>Additional data may be collected for improvement purposes</li>
                    <li>Services may have different security or privacy features</li>
                    <li>Specific terms will be provided before participation</li>
                </ul>

                <h3>25.2 Research and Development</h3>
                <p>We may use aggregated and anonymized data for:</p>
                <ul>
                    <li>Research and development of new features</li>
                    <li>Industry reports and benchmarks</li>
                    <li>Academic research partnerships</li>
                    <li>Open source contributions</li>
                </ul>

                <h2>26. Definitions</h2>
                <p>Key terms used in this Privacy Policy:</p>
                <ul>
                    <li><strong>"Personal Data/Information":</strong> Information that identifies or could identify you
                    </li>
                    <li><strong>"Processing":</strong> Any operation performed on personal data</li>
                    <li><strong>"Controller":</strong> Entity that determines purposes and means of processing</li>
                    <li><strong>"Processor":</strong> Entity that processes data on behalf of a controller</li>
                    <li><strong>"Automated Digital Transformation":</strong> AI-powered business process automation and
                        optimization
                    </li>
                    <li><strong>"Services":</strong> All products, services, and platforms offered by Intellommerce</li>
                </ul>

                <h2>27. Acknowledgment and Consent</h2>
                <p>By using our services, you acknowledge that you have read and understood this Privacy Policy. Where
                    we rely on consent as a legal basis for processing your personal information, you have the right to
                    withdraw your consent at any time by contacting us at privacy@intellommerce.com.</p>

                <h2>28. Version History</h2>
                <p>Significant updates to this Privacy Policy:</p>
                <ul>
                    <li>Fri Aug 22, 2025 - Comprehensive update for global privacy compliance</li>
                    <li>Tue 19 Aug, 2025 - Initial privacy policy</li>
                </ul>

                <hr style={{margin: '40px 0'}}/>

                <p style={{textAlign: 'center', fontStyle: 'italic'}}>
                    This Privacy Policy was last updated on Fri Aug 22, 2025.<br/>
                    © {currentYear} Intellommerce, Inc. All rights reserved.<br/>
                    Privacy Policy Version 2.0
                </p>
            </div>
        </div>
    );
};

export default PrivacyPolicy;